import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api, assetUrl } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import PublicLayout from "@/components/PublicLayout";
import { GridFour, MapPin } from "@phosphor-icons/react";

export default function PublicMap() {
  useSeo({
    title: "Photo map — PhotoPulse.pro",
    description: "Discover public photos by location on an interactive world map of nature, landscapes and cityscapes.",
  });
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/map").then((r) => setPoints(r.data.points)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <PublicLayout>
      <div className="mx-auto max-w-6xl px-5 pt-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="overline text-[#F27A54]">Discover by place</p>
            <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Photo map</h1>
            <p className="text-sm text-muted-foreground">{loading ? "Loading pins…" : `${points.length.toLocaleString()} located photos`}</p>
          </div>
          <Link to="/explore" data-testid="map-to-grid"
            className="flex items-center gap-1.5 rounded-full border border-[#F27A54] px-4 py-2 text-sm font-bold text-[#F27A54] hover:bg-blue-50">
            <GridFour size={16} weight="fill" /> Grid view
          </Link>
        </div>
      </div>

      <div className="mx-auto mt-5 max-w-6xl px-5 pb-10">
        <div className="h-[70vh] overflow-hidden rounded-xl border border-border" data-testid="public-map">
          <MapContainer center={[25, 10]} zoom={2} scrollWheelZoom className="h-full w-full">
            <TileLayer attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {points.map((p) => (
              <CircleMarker key={p.id} center={[p.lat, p.lon]} radius={7}
                pathOptions={{ color: "#F27A54", fillColor: "#F27A54", fillOpacity: 0.75, weight: 2 }}>
                <Popup>
                  <Link to={`/p/${p.id}`} data-testid={`map-popup-${p.id}`} className="block w-44">
                    <img src={assetUrl(p.image_url)} alt={p.title} className="h-24 w-full rounded object-cover" />
                    <p className="mt-1.5 text-sm font-bold leading-snug text-zinc-900">{p.title}</p>
                    {p.city && <p className="flex items-center gap-1 text-xs text-zinc-500"><MapPin size={11} weight="fill" />{[p.city, p.country].filter(Boolean).join(", ")}</p>}
                    <span className="mt-1 inline-block text-xs font-semibold text-[#F27A54]">View photo →</span>
                  </Link>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      </div>
    </PublicLayout>
  );
}
